import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Overlay, Popover } from 'react-bootstrap';
import { Button } from '@praktio/common-ui';
import { Constants } from '@praktio/common';

import { getNext, getPrevious } from '../../ducks/slidesLitmosReducer';
import { update as updateQuiz } from '../../ducks/quizLitmosReducer';
import { updatePlayer } from '../../ducks/explainerReducer';
import { submitForSlide } from '../../ducks/learnerReducer';
import { RootState } from '../../ducks/configureStore';
import { getDefaultInstructions, isEndOfSlide, isExplainerSlide } from '../../utils/slide';

import './ModuleBottomBar.scss';
import { WindowSizes } from '../../utils/constants';

const { TemplateTypes, CustomNavType } = Constants;
const { Conclusions } = TemplateTypes;

interface IProps {
  windowSize: WindowSizes;
}

const ModuleBottomBar = ({ windowSize }: IProps) => {
  const dispatch = useDispatch();

  // LOCAL STATE
  const [showBackOverlay, setShowBackOverlay] = useState(false);

  // REDUX STATE
  const { slidesLitmos, coursesLitmos, quizLitmos, explanation, learner } = useSelector(
    (state: RootState) => {
      const { slidesLitmos, coursesLitmos, quizLitmos, explanation, learner } = state;
      return {
        slidesLitmos,
        coursesLitmos,
        quizLitmos,
        explanation,
        learner,
      };
    },
  );

  const { currentSlide } = slidesLitmos;
  const { selected, showSubmit, disableNext, advance } = quizLitmos;
  const { learnerId, isSubmitting } = learner;
  const { player } = explanation;

  const { type: slideType, _id } = currentSlide || {};

  const isPreview = learnerId && learnerId === 'SlidePreviewer';
  const isEndOfExplainerSlide =
    isExplainerSlide(slideType) && isEndOfSlide(player) && player.autoAdvance; // Used below to prevent double request (next click + auto-advance) causing slide skip (Jira: PA-2463)

  const { data } = slidesLitmos;
  const { isPre, isPost } = data || {};
  const isPrePostModule = isPre || isPost;
  const isCustomNav = slideType === CustomNavType;

  const { learnerData, isLast } = currentSlide || {};
  const { unlocked, number } = learnerData || {};
  const slideUnlocked = unlocked || isPreview || false;
  const instructions =
    currentSlide?.instructions ||
    getDefaultInstructions(coursesLitmos, currentSlide) ||
    'Before you may submit, complete the exercise.';

  const prevAdvanceRef = useRef(false);
  const overlayTarget = useRef(null);

  const moveNext = useCallback(() => {
    // Stop player
    dispatch(updatePlayer({ playing: false }));

    // Disable next/submit buttons
    dispatch(
      updateQuiz({
        choicesShouldPulse: false,
        tooltipEngaged: false,
        disableNext: true,
        showSubmit: false,
      }),
    );

    // Get next slide. This call will also clear the quiz data.
    dispatch(getNext());
  }, [dispatch]);

  useEffect(() => {
    // For Auto-Advance.
    // 'advance' initializes as false. If 'advance' becomes true but the last value of it was false,
    // adance to next screen and set 'advance' back to false.
    if (!isLast && advance && !prevAdvanceRef.current) {
      moveNext();
      dispatch(
        updateQuiz({
          advance: false,
        }),
      );
    }

    prevAdvanceRef.current = !!advance;
  }, [dispatch, advance, prevAdvanceRef, isLast, moveNext]);

  const previousClick = () => {
    // Disable tooltip pulse
    dispatch(
      updateQuiz({
        choicesShouldPulse: false,
        tooltipEngaged: false,
      }),
    );
    // Stop player
    dispatch(updatePlayer({ playing: false }));
    // Get previous slide
    dispatch(getPrevious());
  };

  const submitClick = () => {
    // Disable tooltip pulse
    dispatch(
      updateQuiz({
        choicesShouldPulse: false,
        tooltipEngaged: false,
      }),
    );

    if (learnerId && currentSlide && selected) {
      dispatch(submitForSlide(learnerId, currentSlide, selected));
    }
  };

  const hideBackOverlay = () => {
    setShowBackOverlay(false);
  };

  const renderSubmitButton = () => {
    if (!showSubmit) {
      return null;
    }

    const includeTooltip =
      (selected && Array.isArray(selected) && selected.length === 0) ||
      (!selected && selected !== '0');

    const isDisabled =
      isSubmitting ||
      (!selected && selected !== '0') ||
      selected?.length === 0 ||
      isEndOfExplainerSlide;

    return (
      <Button
        tooltip={includeTooltip ? `Before you may submit: ${instructions}` : undefined}
        tooltipPlacement={includeTooltip ? 'top' : undefined}
        onClick={!isSubmitting && (selected || selected === '0') ? submitClick : undefined}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-label="Submit"
        showSpinner={isSubmitting}
        overlaySpinner
      >
        {windowSize !== WindowSizes.Small && <span>SUBMIT</span>}
        <span className="material-icons" aria-hidden="true">
          send
        </span>
      </Button>
    );
  };

  const renderNextButton = () => {
    if (disableNext) {
      return null;
    }

    return (
      <Button
        tooltip={
          !slideUnlocked
            ? 'Moving to the next activity is disabled until you have completed this activity.'
            : undefined
        }
        tooltipPlacement={!slideUnlocked ? 'top' : undefined}
        aria-label="Next"
        onClick={slideUnlocked ? moveNext : undefined}
        disabled={!slideUnlocked || isEndOfExplainerSlide}
        tooltipOnClick={!slideUnlocked}
        btnStyle="primary"
      >
        {windowSize !== WindowSizes.Small && <span>NEXT</span>}
        <span className="material-icons" aria-hidden="true">
          arrow_forward
        </span>
      </Button>
    );
  };

  const renderBackButton = () => {
    const { type } = currentSlide || {};
    const showPrevious = number && number > 1;

    if (!showPrevious) {
      return null;
    }
    return showBackOverlay && type === Conclusions && isPrePostModule ? (
      <>
        <Button ref={overlayTarget} onClick={previousClick} className="dark" aria-label="Back">
          <span className="material-icons" aria-hidden="true">
            arrow_back
          </span>
          BACK
        </Button>
        <Overlay placement="top" target={overlayTarget.current} show={true}>
          <Popover id="tooltipBack">
            <Popover.Content>You may review, but not retake, this test module.</Popover.Content>
            <div className="close-tooltip">
              <span className="material-icons-outlined" onClick={hideBackOverlay}>
                close
              </span>
            </div>
          </Popover>
        </Overlay>
      </>
    ) : (
      <Button onClick={previousClick} className="dark">
        <span className="material-icons" aria-hidden="true">
          arrow_back
        </span>

        {windowSize !== WindowSizes.Small && <span>BACK</span>}
      </Button>
    );
  };

  const isActualSlide = !isCustomNav;

  if (isActualSlide && !_id) {
    return <div></div>;
  }

  const submitButton = isActualSlide ? renderSubmitButton() : null;
  const backButton = isActualSlide ? renderBackButton() : null;
  const nextButton = isActualSlide ? renderNextButton() : null;

  return (
    <div className="ModuleBottomBar">
      <div className="left-container">{backButton}</div>
      {/* Next/Submit Button */}
      {/* Should be clickable if on explainer slide at any time. */}
      {/* Should be disabled if on an exercise slide until a selection is made. */}
      {/* Should read Submit once a selection is made. */}
      {/* Should read Next when "done". Either correct choice(s) made or out of tries. */}
      {/* Should be hidden when on last slide. "Next Module" Button will replace it, served from LMS. */}
      <div className="right-container">
        {nextButton}
        {submitButton}
      </div>
    </div>
  );
};

export default ModuleBottomBar;
